import { withListOverflow } from "../components/ListOverflow.jsx";
export const DecisionsRegisterPage = withListOverflow(DecisionsListOwner);
// src/v2/leases/DecisionsRegisterPage.jsx
//
// P-15: DECISIONS-REGISTER-UI
//
// Full portfolio-wide decisions register page.
// Canonical route: /radar/decisions
//
// Features: search, status filter chips, inline status edit, new decision
// form (modal), soft-delete + restore, role-gated write controls.

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { T } from "../../app/tokens.js";
import {
  getDecisions,
  getDeletedDecisions,
  createDecision,
  updateDecision,
  deleteDecision,
  restoreDecision,
} from "../../data/decisionsClient.js";
import { getPortfolioLeases } from "../../data/leasesPortfolioClient.js";
import { DecisionStatusChip } from "./RenewalDecisionModal.jsx";
import { matchesFoldedQuery } from "../../utils/textSearch.js";

// ─── Helpers ──────────────────────────────────────────────────────────────────

function fmtDate(d) {
  if (!d) return "—";
  const raw = d instanceof Date ? d.toISOString() : String(d);
  const iso = raw.slice(0, 10);
  if (!iso || iso === "Inva") return "—";
  const dt = new Date(iso + "T12:00:00Z");
  if (Number.isNaN(dt.getTime())) return "—";
  return dt.toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric", timeZone: "UTC" });
}

const STATUS_OPTIONS = [
  { value: "logged",    label: "Logged"    },
  { value: "in_motion", label: "In motion" },
  { value: "verified",  label: "Verified"  },
  { value: "not_yet",   label: "Not yet"   },
];

const CHIP_FILTER_LABELS = ["All", "Logged", "In motion", "Verified", "Not yet"];
const CHIP_FILTER_VALUES = ["all", "logged", "in_motion", "verified", "not_yet"];

function statusLabelToValue(label) {
  const idx = CHIP_FILTER_LABELS.indexOf(label);
  return idx >= 0 ? CHIP_FILTER_VALUES[idx] : "all";
}

// ─── Status chip as inline select (editor/admin) ──────────────────────────────

function StatusSelect({ row, onUpdate }) {
  const [busy, setBusy] = useState(false);

  async function handleChange(e) {
    const newStatus = e.target.value;
    if (newStatus === row.status || busy) return;
    setBusy(true);
    try {
      await onUpdate(row.id, { status: newStatus });
    } finally {
      setBusy(false);
    }
  }

  const STATUS_STYLE = {
    logged:    { bg: T.blueSoft,   fg: T.blue         },
    in_motion: { bg: T.ragAmberBg, fg: T.ragAmberText },
    verified:  { bg: T.ragGreenBg, fg: T.ragGreenText },
    not_yet:   { bg: T.lightGray,  fg: T.mid          },
  };
  const s = STATUS_STYLE[row.status] ?? STATUS_STYLE.logged;

  return (
    <select
      data-testid={`status-select-${row.id}`}
      value={row.status}
      onChange={handleChange}
      disabled={busy}
      style={{
        fontSize:     T.fsXxs,
        fontWeight:   T.fwSemibold,
        background:   s.bg,
        color:        s.fg,
        border:       "none",
        borderRadius: 5,
        padding:      "1px 6px",
        cursor:       "pointer",
        appearance:   "none",
        WebkitAppearance: "none",
        outline:      "none",
        whiteSpace:   "nowrap",
      }}
    >
      {STATUS_OPTIONS.map((opt) => (
        <option key={opt.value} value={opt.value}>{opt.label}</option>
      ))}
    </select>
  );
}

// ─── Decision row ─────────────────────────────────────────────────────────────

function DecisionRow({ row, canEdit, onUpdateStatus, onDelete }) {
  const leaseChip = [row.locationCity, row.locationAlias]
    .filter(Boolean)
    .join(" · ") || row.entityId || "—";

  return (
    <tr
      data-testid={`decisions-row-${row.id}`}
      style={{ borderBottom: `1px solid ${T.border}` }}
    >
      {/* Decision */}
      <td style={{ padding: "10px 12px", verticalAlign: "middle", maxWidth: 280 }}>
        <div style={{ fontSize: T.fsSm5, fontWeight: T.fwSemibold, color: T.dark, lineHeight: 1.4 }}>
          {row.decision || "—"}
        </div>
        <span
          style={{
            display:      "inline-block",
            marginTop:    4,
            fontSize:     T.fsXxs,
            fontWeight:   T.fwSemibold,
            background:   T.blueSoft,
            color:        T.blue,
            borderRadius: 5,
            padding:      "1px 7px",
            whiteSpace:   "nowrap",
            maxWidth:     220,
            overflow:     "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {leaseChip}
        </span>
        {row.playbookRunId && (
          <a
            href={`/playbooks/runs/${encodeURIComponent(row.playbookRunId)}`}
            data-testid={`open-playbook-decision-${row.id}`}
            style={{
              display: "inline-block", marginTop: 5,
              color: T.blue, fontSize: T.fsXs, fontWeight: T.fwSemibold,
              textDecoration: "none",
            }}
          >
            Open Playbook →
          </a>
        )}
      </td>

      {/* Expected action */}
      <td style={{ padding: "10px 12px", verticalAlign: "middle", fontSize: T.fsXs, color: T.mid, maxWidth: 220 }}>
        <span style={{
          display:         "-webkit-box",
          WebkitLineClamp: 2,
          WebkitBoxOrient: "vertical",
          overflow:        "hidden",
        }}>
          {row.expectedAction || "—"}
        </span>
      </td>

      {/* Owner */}
      <td style={{ padding: "10px 12px", verticalAlign: "middle", fontSize: T.fsXs, color: T.dark, whiteSpace: "nowrap" }}>
        {row.ownerName || <span style={{ color: T.mid }}>—</span>}
      </td>

      {/* Target */}
      <td style={{ padding: "10px 12px", verticalAlign: "middle", fontSize: T.fsXs, color: T.dark, whiteSpace: "nowrap" }}>
        {fmtDate(row.targetDate)}
      </td>

      {/* Status */}
      <td style={{ padding: "10px 12px", verticalAlign: "middle", whiteSpace: "nowrap" }}>
        {canEdit
          ? <StatusSelect row={row} onUpdate={onUpdateStatus} />
          : <DecisionStatusChip status={row.status} />
        }
      </td>

      {/* Actions (editor/admin only) */}
      {canEdit && (
        <td style={{ padding: "10px 8px", verticalAlign: "middle" }}>
          <button
            type="button"
            data-testid={`delete-decision-${row.id}`}
            onClick={() => onDelete(row.id)}
            title="Delete decision"
            aria-label="Delete decision"
            style={{
              background: "none",
              border:     "none",
              color:      T.mid,
              cursor:     "pointer",
              fontSize:   T.fsXs,
              padding:    "2px 4px",
              lineHeight: 1,
              borderRadius: 4,
            }}
          >
            ✕
          </button>
        </td>
      )}
    </tr>
  );
}

// ─── Empty state ──────────────────────────────────────────────────────────────

function EmptyState({ canEdit, hasFilter }) {
  if (hasFilter) {
    return (
      <tr>
        <td
          colSpan={99}
          style={{ padding: "40px 20px", textAlign: "center", color: T.mid, fontSize: T.fsSm, fontStyle: "italic" }}
        >
          No decisions match your current filters.
        </td>
      </tr>
    );
  }
  return (
    <tr>
      <td
        colSpan={99}
        style={{ padding: "40px 20px", textAlign: "center", color: T.mid, fontSize: T.fsSm }}
      >
        {canEdit
          ? <>No decisions logged yet. Click <strong>+ New decision</strong> to begin.</>
          : "No decisions logged yet. Contact your administrator to add decisions."}
      </td>
    </tr>
  );
}

// ─── New decision modal ───────────────────────────────────────────────────────

const DEFAULT_FORM = {
  entityId:       "",
  decision:       "",
  expectedAction: "",
  ownerName:      "",
  targetDate:     "",
  status:         "logged",
};

function NewDecisionModal({ onClose, onCreated }) {
  const [form,   setForm]   = useState(DEFAULT_FORM);
  const [leases, setLeases] = useState([]);
  const [users,  setUsers]  = useState([]);
  const [saving, setSaving] = useState(false);
  const [error,  setError]  = useState(null);

  // Load lease + user pickers once
  useEffect(() => {
    getPortfolioLeases()
      .then((rows) => {
        if (!Array.isArray(rows)) return;
        setLeases(rows.map((l) => ({
          id:    l.id,
          label: [l.city, l.locationAlias].filter(Boolean).join(" · ") || l.id,
        })));
      })
      .catch(() => {});

    fetch("/api/plan/users", { credentials: "include" })
      .then((r) => r.ok ? r.json() : [])
      .then((rows) => {
        if (Array.isArray(rows)) setUsers(rows.map((u) => u.displayName).filter(Boolean));
      })
      .catch(() => {});
  }, []);

  function setField(key, val) {
    setForm((f) => ({ ...f, [key]: val }));
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (saving) return;
    if (!form.entityId) { setError("Please select a lease."); return; }
    if (!form.decision.trim()) { setError("Decision text is required."); return; }
    setError(null);
    setSaving(true);
    try {
      const payload = {
        entityType:     "lease",
        entityId:       form.entityId,
        decision:       form.decision.trim(),
        expectedAction: form.expectedAction.trim() || undefined,
        ownerName:      form.ownerName || undefined,
        targetDate:     form.targetDate || undefined,
        status:         form.status,
      };
      const created = await createDecision(payload);
      onCreated(created);
      onClose();
    } catch (err) {
      setError(err.message || "Failed to create decision.");
    } finally {
      setSaving(false);
    }
  }

  const inputStyle = {
    width:        "100%",
    fontSize:     T.fsSm,
    padding:      "7px 9px",
    borderRadius: 7,
    border:       `1px solid ${T.border}`,
    boxSizing:    "border-box",
    fontFamily:   "inherit",
  };

  const labelStyle = {
    display:      "block",
    fontSize:     T.fsXs,
    fontWeight:   T.fwSemibold,
    color:        T.dark,
    marginBottom: 4,
  };

  return (
    <div
      data-testid="new-decision-modal-scrim"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      style={{
        position:       "fixed",
        inset:          0,
        background:     "rgba(10,24,46,0.45)",
        zIndex:         1000,
        display:        "flex",
        alignItems:     "center",
        justifyContent: "center",
        padding:        "24px 16px",
      }}
    >
      <div
        data-testid="new-decision-modal"
        style={{
          background:    T.card,
          borderRadius:  14,
          boxShadow:     "0 20px 60px rgba(10,24,46,0.25)",
          width:         "100%",
          maxWidth:      520,
          maxHeight:     "90vh",
          display:       "flex",
          flexDirection: "column",
          overflow:      "hidden",
        }}
      >
        {/* Header */}
        <div style={{
          padding:        "18px 20px 14px",
          borderBottom:   `1px solid ${T.border}`,
          display:        "flex",
          alignItems:     "flex-start",
          justifyContent: "space-between",
          flexShrink:     0,
        }}>
          <div>
            <div style={{ fontSize: T.fsMd, fontWeight: T.fwBold, color: T.dark }}>
              Log a decision
            </div>
            <div style={{ fontSize: T.fsXs, color: T.mid, marginTop: 3 }}>
              Record intent, owner, and status for a lease decision.
            </div>
          </div>
          <button
            type="button"
            data-testid="new-decision-modal-close"
            onClick={onClose}
            aria-label="Close"
            style={{
              background: "none",
              border:     "none",
              color:      T.mid,
              fontSize:   T.fsLg,
              cursor:     "pointer",
              padding:    "0 4px",
              lineHeight: 1,
              marginTop:  -2,
            }}
          >
            ×
          </button>
        </div>

        {/* Body */}
        <form
          onSubmit={handleSubmit}
          style={{ flex: 1, overflowY: "auto", padding: "18px 20px" }}
        >
          {error && (
            <div style={{
              marginBottom: 14,
              padding:      "9px 12px",
              background:   T.ragRedBg,
              color:        T.ragRed,
              borderRadius: 7,
              fontSize:     T.fsXs,
            }}>
              {error}
            </div>
          )}

          {/* Related to (lease picker) */}
          <div style={{ marginBottom: 12 }}>
            <label style={labelStyle}>
              Related to <span style={{ fontWeight: T.fwRegular, color: T.mid }}>(lease)</span>
            </label>
            <select
              data-testid="new-decision-lease-select"
              value={form.entityId}
              onChange={(e) => setField("entityId", e.target.value)}
              style={{ ...inputStyle, color: form.entityId ? T.dark : T.mid }}
            >
              <option value="">Select a lease…</option>
              {leases.map((l) => (
                <option key={l.id} value={l.id}>{l.label}</option>
              ))}
            </select>
          </div>

          {/* Decision text */}
          <div style={{ marginBottom: 12 }}>
            <label style={labelStyle}>Decision</label>
            <textarea
              data-testid="new-decision-text"
              value={form.decision}
              onChange={(e) => setField("decision", e.target.value)}
              placeholder="What was decided?"
              rows={3}
              style={{ ...inputStyle, resize: "vertical" }}
            />
          </div>

          {/* Expected action */}
          <div style={{ marginBottom: 12 }}>
            <label style={labelStyle}>
              Expected action <span style={{ fontWeight: T.fwRegular, color: T.mid }}>(optional)</span>
            </label>
            <textarea
              data-testid="new-decision-expected-action"
              value={form.expectedAction}
              onChange={(e) => setField("expectedAction", e.target.value)}
              placeholder="What steps should follow?"
              rows={2}
              style={{ ...inputStyle, resize: "vertical" }}
            />
          </div>

          {/* Owner + Target (2-col) */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 12 }}>
            <div>
              <label style={labelStyle}>
                Owner <span style={{ fontWeight: T.fwRegular, color: T.mid }}>(optional)</span>
              </label>
              <select
                data-testid="new-decision-owner-select"
                value={form.ownerName}
                onChange={(e) => setField("ownerName", e.target.value)}
                style={{ ...inputStyle, color: form.ownerName ? T.dark : T.mid }}
              >
                <option value="">Unassigned</option>
                {users.map((name) => (
                  <option key={name} value={name}>{name}</option>
                ))}
              </select>
            </div>
            <div>
              <label style={labelStyle}>
                Target date <span style={{ fontWeight: T.fwRegular, color: T.mid }}>(optional)</span>
              </label>
              <input
                type="date"
                data-testid="new-decision-target-date"
                value={form.targetDate}
                onChange={(e) => setField("targetDate", e.target.value)}
                style={inputStyle}
              />
            </div>
          </div>

          {/* Status selector */}
          <div style={{ marginBottom: 18 }}>
            <label style={labelStyle}>Status</label>
            <div style={{ display: "flex", gap: 6 }}>
              {STATUS_OPTIONS.map((opt) => {
                const active = form.status === opt.value;
                return (
                  <button
                    key={opt.value}
                    type="button"
                    data-testid={`new-decision-status-${opt.value}`}
                    onClick={() => setField("status", opt.value)}
                    style={{
                      flex:         1,
                      padding:      "5px 0",
                      borderRadius: 7,
                      border:       `1.5px solid ${active ? T.blue : T.border}`,
                      background:   active ? T.blueSoft : T.card,
                      color:        active ? T.blue : T.mid,
                      fontSize:     T.fsXs,
                      fontWeight:   T.fwSemibold,
                      cursor:       "pointer",
                      transition:   "all .1s",
                    }}
                  >
                    {opt.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Footer */}
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}>
            <button
              type="button"
              onClick={onClose}
              style={{
                padding:      "7px 16px",
                borderRadius: 7,
                border:       `1px solid ${T.border}`,
                background:   T.card,
                color:        T.mid,
                fontSize:     T.fsSm,
                cursor:       "pointer",
              }}
            >
              Cancel
            </button>
            <button
              type="submit"
              data-testid="new-decision-submit"
              disabled={saving}
              style={{
                padding:      "7px 20px",
                borderRadius: 7,
                border:       "none",
                background:   saving ? T.mid : T.blue,
                color:        T.white,
                fontSize:     T.fsSm,
                fontWeight:   T.fwSemibold,
                cursor:       saving ? "default" : "pointer",
              }}
            >
              {saving ? "Saving…" : "Log decision"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─── Deleted row ──────────────────────────────────────────────────────────────

function DeletedRow({ row, onRestore }) {
  const [busy, setBusy] = useState(false);

  async function handleRestore() {
    if (busy) return;
    setBusy(true);
    try {
      await onRestore(row.id);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      data-testid={`deleted-decision-${row.id}`}
      style={{
        display:        "flex",
        alignItems:     "center",
        justifyContent: "space-between",
        padding:        "9px 14px",
        borderBottom:   `1px solid ${T.border}`,
        gap:            12,
      }}
    >
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: T.fsXs, color: T.dark, fontWeight: T.fwSemibold, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {row.decision || "—"}
        </div>
        {(row.locationCity || row.locationAlias) && (
          <div style={{ fontSize: T.fsXxs, color: T.mid, marginTop: 2 }}>
            {[row.locationCity, row.locationAlias].filter(Boolean).join(" · ")}
          </div>
        )}
      </div>
      <button
        type="button"
        data-testid={`restore-decision-${row.id}`}
        onClick={handleRestore}
        disabled={busy}
        style={{
          padding:      "4px 12px",
          borderRadius: 6,
          border:       `1px solid ${T.border}`,
          background:   T.card,
          color:        T.blue,
          fontSize:     T.fsXs,
          fontWeight:   T.fwSemibold,
          cursor:       busy ? "default" : "pointer",
          whiteSpace:   "nowrap",
          flexShrink:   0,
        }}
      >
        {busy ? "Restoring…" : "Restore"}
      </button>
    </div>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

/**
 * @param {object} props
 * @param {string} [props.currentRole]  "admin"|"editor"|"viewer"|null
 */
function DecisionsListOwner({ currentRole, embedded = false }) {
  const canEdit = currentRole === "admin" || currentRole === "editor";

  const [decisions,        setDecisions]        = useState([]);
  const [deletedDecisions, setDeletedDecisions] = useState([]);
  const [loading,          setLoading]          = useState(true);
  const [error,            setError]            = useState(null);
  const [search,           setSearch]           = useState("");
  const [statusFilter,     setStatusFilter]     = useState("all");
  const [showForm,         setShowForm]         = useState(false);
  const [showDeleted,      setShowDeleted]      = useState(false);

  // Initial load
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const fetches = [getDecisions()];
    if (canEdit) fetches.push(getDeletedDecisions());

    Promise.all(fetches)
      .then(([active, deleted]) => {
        if (cancelled) return;
        setDecisions(Array.isArray(active) ? active : []);
        if (deleted) setDeletedDecisions(Array.isArray(deleted) ? deleted : []);
        setLoading(false);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err.message || "Failed to load decisions.");
        setLoading(false);
      });
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Client-side filtering
  const searchLower = search;
  const filtered = useMemo(() => {
    let rows = decisions;
    if (searchLower) {
      rows = rows.filter((d) =>
        [d.decision, d.expectedAction, d.locationCity, d.locationAlias]
          .filter(Boolean)
          .some((s) => matchesFoldedQuery(searchLower, s))
      );
    }
    if (statusFilter !== "all") {
      rows = rows.filter((d) => d.status === statusFilter);
    }
    return rows;
  }, [decisions, searchLower, statusFilter]);

  // Chip counts (post-search, pre-status-filter)
  const chipCounts = useMemo(() => {
    const base = searchLower
      ? decisions.filter((d) =>
          [d.decision, d.expectedAction, d.locationCity, d.locationAlias]
            .filter(Boolean)
            .some((s) => matchesFoldedQuery(searchLower, s))
        )
      : decisions;

    const counts = { all: base.length, logged: 0, in_motion: 0, verified: 0, not_yet: 0 };
    for (const d of base) {
      if (counts[d.status] != null) counts[d.status]++;
    }
    return counts;
  }, [decisions, searchLower]);

  // Handlers
  const handleCreated = useCallback((row) => {
    setDecisions((prev) => [row, ...prev]);
  }, []);

  const handleUpdateStatus = useCallback(async (id, patch) => {
    const updated = await updateDecision(id, patch);
    setDecisions((prev) => prev.map((d) => d.id === id ? { ...d, ...updated } : d));
  }, []);

  const handleDelete = useCallback(async (id) => {
    await deleteDecision(id);
    setDecisions((prev) => {
      const row = prev.find((d) => d.id === id);
      if (row) setDeletedDecisions((del) => [row, ...del]);
      return prev.filter((d) => d.id !== id);
    });
  }, []);

  const handleRestore = useCallback(async (id) => {
    const restored = await restoreDecision(id);
    setDeletedDecisions((prev) => prev.filter((d) => d.id !== id));
    setDecisions((prev) => [restored, ...prev]);
  }, []);

  // Table column headers
  const COL_HEADERS = ["Decision", "Expected action", "Owner", "Target", "Status"];

  return (
    <div
      data-testid="decisions-register-page"
      style={{
        padding: embedded ? "0 0 8px" : "24px 24px",
        maxWidth: embedded ? "none" : 1100,
        margin: embedded ? 0 : "0 auto",
      }}
    >
      {/* Page heading */}
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 6 }}>
        <div>
          <h1 style={{
            fontSize:      T.fsLg5,
            fontWeight:    T.fwBold,
            color:         T.dark,
            margin:        0,
            letterSpacing: -0.3,
          }}>
            Decisions
          </h1>
          <p style={{ fontSize: T.fsSm, color: T.mid, marginTop: 4, marginBottom: 0 }}>
            Decisions logged across the portfolio – intent, owner, and status.
          </p>
        </div>
        {canEdit && (
          <button
            type="button"
            data-testid="new-decision-btn"
            onClick={() => setShowForm(true)}
            style={{
              marginTop:    4,
              padding:      "7px 16px",
              borderRadius: 8,
              border:       "none",
              background:   T.blue,
              color:        T.white,
              fontSize:     T.fsSm,
              fontWeight:   T.fwSemibold,
              cursor:       "pointer",
              whiteSpace:   "nowrap",
              flexShrink:   0,
            }}
          >
            + New decision
          </button>
        )}
      </div>

      <div style={{ margin: "16px 0 16px", width: "100%", height: 1, background: T.border }} />

      {/* Toolbar: search + status filters */}
      <div style={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: "8px 12px", marginBottom: 16 }}>
        {/* Search */}
        <input
          type="search"
          data-testid="decisions-search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search decisions…"
          style={{
            padding:      "6px 11px",
            borderRadius: 8,
            border:       `1px solid ${T.border}`,
            fontSize:     T.fsSm5,
            color:        T.dark,
            background:   T.card,
            width:        220,
            outline:      "none",
            boxSizing:    "border-box",
          }}
        />

        {/* Status filter chips */}
        <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
          {CHIP_FILTER_LABELS.map((label, idx) => {
            const val = CHIP_FILTER_VALUES[idx];
            const active = statusFilter === val;
            const count = chipCounts[val] ?? chipCounts.all;
            return (
              <button
                key={val}
                type="button"
                data-testid={`filter-chip-${val}`}
                onClick={() => setStatusFilter(val)}
                style={{
                  padding:      "4px 10px",
                  borderRadius: 20,
                  border:       `1.5px solid ${active ? T.blue : T.border}`,
                  background:   active ? T.blueSoft : T.card,
                  color:        active ? T.blue : T.mid,
                  fontSize:     T.fsXs,
                  fontWeight:   T.fwSemibold,
                  cursor:       "pointer",
                  whiteSpace:   "nowrap",
                  transition:   "all .1s",
                }}
              >
                {label}{val !== "all" ? ` · ${count}` : ` · ${chipCounts.all}`}
              </button>
            );
          })}
        </div>
      </div>

      {/* Error state */}
      {error && (
        <div style={{ padding: "16px", background: T.ragRedBg, borderRadius: 8, color: T.ragRed, fontSize: T.fsSm, marginBottom: 16 }}>
          {error}
        </div>
      )}

      {/* Loading state */}
      {loading && (
        <div style={{ padding: "32px 0", textAlign: "center", color: T.mid, fontSize: T.fsSm, fontStyle: "italic" }}>
          Loading decisions…
        </div>
      )}

      {/* Register table */}
      {!loading && !error && (
        <div style={{
          border:       `1px solid ${T.border}`,
          borderRadius: 10,
          overflowX:    "auto",
          background:   T.card,
          boxShadow:    "0 1px 4px rgba(16,36,58,.06)",
          marginBottom: 20,
        }}>
          <table
            data-testid="decisions-register-table"
            style={{ width: "100%", borderCollapse: "collapse" }}
          >
            <thead>
              <tr style={{ background: T.tableHeadBg }}>
                {COL_HEADERS.map((h) => (
                  <th
                    key={h}
                    style={{
                      padding:       "9px 12px",
                      textAlign:     "left",
                      fontSize:      T.fsXxs,
                      fontWeight:    T.fwSemibold,
                      color:         T.mid,
                      textTransform: "uppercase",
                      letterSpacing: ".04em",
                      borderBottom:  `1px solid ${T.border}`,
                      whiteSpace:    "nowrap",
                    }}
                  >
                    {h}
                  </th>
                ))}
                {/* Actions column header (editor/admin only) */}
                {canEdit && <th style={{ padding: "9px 8px", borderBottom: `1px solid ${T.border}` }} />}
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0
                ? <EmptyState canEdit={canEdit} hasFilter={search !== "" || statusFilter !== "all"} />
                : filtered.map((row) => (
                    <DecisionRow
                      key={row.id}
                      row={row}
                      canEdit={canEdit}
                      onUpdateStatus={handleUpdateStatus}
                      onDelete={handleDelete}
                    />
                  ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Recently deleted (editor/admin) */}
      {!loading && !error && canEdit && deletedDecisions.length > 0 && (
        <div style={{ marginBottom: 24 }}>
          <button
            type="button"
            data-testid="toggle-deleted-section"
            onClick={() => setShowDeleted((v) => !v)}
            style={{
              display:    "flex",
              alignItems: "center",
              gap:        6,
              background: "none",
              border:     "none",
              cursor:     "pointer",
              fontSize:   T.fsXs,
              fontWeight: T.fwSemibold,
              color:      T.mid,
              padding:    "0 0 8px",
            }}
          >
            <span style={{
              display:     "inline-block",
              transform:   showDeleted ? "rotate(90deg)" : "rotate(0deg)",
              transition:  "transform .15s",
              fontSize:    T.fsXxs,
            }}>▶</span>
            Recently deleted ({deletedDecisions.length})
          </button>

          {showDeleted && (
            <div style={{
              border:       `1px solid ${T.border}`,
              borderRadius: 10,
              overflow:     "hidden",
              background:   T.card,
              boxShadow:    "0 1px 4px rgba(16,36,58,.06)",
            }}>
              {deletedDecisions.map((row) => (
                <DeletedRow key={row.id} row={row} onRestore={handleRestore} />
              ))}
            </div>
          )}
        </div>
      )}

      {/* Footer */}
      {!loading && !error && (
        <div style={{ fontSize: T.fsXxs, color: T.mid, marginTop: 8 }}>
          {filtered.length} decision{filtered.length !== 1 ? "s" : ""}
          {statusFilter !== "all" || search
            ? ` shown · ${decisions.length} total`
            : " logged across the portfolio"
          }
          {" · "}Every decision + status change is recorded to audit_log (actor · timestamp).
        </div>
      )}

      {/* New decision modal */}
      {showForm && (
        <NewDecisionModal
          onClose={() => setShowForm(false)}
          onCreated={handleCreated}
        />
      )}
    </div>
  );
}
